import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Tag, TrendingUp, TrendingDown, AlertTriangle, Sparkles } from 'lucide-react';
import { motion, fadeInUp, staggerContainer } from '../lib/motion-utils';

interface TriggerStat {
  trigger: string;
  count: number;
  avgRTI: number;
  minRTI: number;
  maxRTI: number;
  trend: 'up' | 'down' | 'neutral';
}

export function TriggerInsights() {
  const [triggerStats, setTriggerStats] = useState<TriggerStat[]>([]);
  const [overallAvgRTI, setOverallAvgRTI] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calculateTriggerStats();
  }, []);

  const calculateTriggerStats = () => {
    const activeRelationshipId = localStorage.getItem('tension_active_relationship');
    const allCheckins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    
    // Filter for active relationship
    const checkins = activeRelationshipId
      ? allCheckins.filter((c: any) => c.relationshipId === activeRelationshipId)
      : allCheckins;

    if (checkins.length === 0) {
      setLoading(false);
      return;
    }

    // Calculate overall average RTI
    const totalRTI = checkins.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0);
    const overallAvg = Math.round(totalRTI / checkins.length);
    setOverallAvgRTI(overallAvg);

    // Group check-ins by trigger
    const triggerMap = new Map<string, number[]>();
    
    checkins.forEach((checkin: any) => {
      if (checkin.triggers && Array.isArray(checkin.triggers)) {
        checkin.triggers.forEach((trigger: string) => {
          if (!triggerMap.has(trigger)) {
            triggerMap.set(trigger, []);
          }
          triggerMap.get(trigger)!.push(checkin.rtiScore || 0);
        });
      }
    });

    // Calculate stats for each trigger
    const stats: TriggerStat[] = [];
    
    triggerMap.forEach((rtiScores, trigger) => {
      if (rtiScores.length === 0) return;
      
      const avgRTI = Math.round(rtiScores.reduce((sum, score) => sum + score, 0) / rtiScores.length);
      const minRTI = Math.min(...rtiScores);
      const maxRTI = Math.max(...rtiScores);
      
      // Determine trend compared to overall average
      let trend: 'up' | 'down' | 'neutral' = 'neutral';
      const diff = avgRTI - overallAvg;
      if (diff > 10) trend = 'up';
      else if (diff < -10) trend = 'down';
      
      stats.push({
        trigger,
        count: rtiScores.length,
        avgRTI,
        minRTI,
        maxRTI,
        trend
      });
    });

    // Sort by count (most common first)
    stats.sort((a, b) => b.count - a.count);
    
    setTriggerStats(stats);
    setLoading(false);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-5 h-5 text-pink-500" />
          <h3 className="text-lg font-light text-neutral-900 dark:text-neutral-100">Trigger Insights</h3>
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">Loading insights...</p>
      </Card>
    );
  }

  if (triggerStats.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-5 h-5 text-pink-500" />
          <h3 className="text-lg font-light text-neutral-900 dark:text-neutral-100">Trigger Insights</h3>
        </div>
        <div className="text-center py-8">
          <Sparkles className="w-12 h-12 text-neutral-300 dark:text-neutral-700 mx-auto mb-3" />
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Start tagging your check-ins to see patterns emerge
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-2">
            Tag your next check-in with life context like "Work Stress" or "Period"
          </p>
        </div>
      </Card>
    );
  }

  const highTensionTriggers = triggerStats.filter(t => t.avgRTI > overallAvgRTI + 10);
  const lowTensionTriggers = triggerStats.filter(t => t.avgRTI < overallAvgRTI - 10);

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-pink-500" aria-hidden="true" />
            <h3 className="text-lg font-light text-neutral-900 dark:text-neutral-100">Trigger Insights</h3>
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            How life context affects your relationship tension
          </p>
        </div>

        {/* Overall baseline */}
        <div className="tension-glow bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-600 dark:text-neutral-400">Your baseline RTI</span>
            <span className="text-2xl font-light text-neutral-900 dark:text-neutral-100">{overallAvgRTI}</span>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
            Average across all check-ins
          </p>
        </div>

        {/* High tension triggers */}
        {highTensionTriggers.length > 0 && (
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="space-y-3"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" aria-hidden="true" />
              <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Higher Tension Triggers</h4>
            </div>
            <div className="space-y-2">
              {highTensionTriggers.map((stat) => (
                <div
                  key={stat.trigger}
                  className="tension-glow bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-orange-900 dark:text-orange-300">{stat.trigger}</span>
                        <span className="text-xs text-orange-600 dark:text-orange-400">×{stat.count}</span>
                      </div>
                      <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                        RTI averages <span className="font-medium">{stat.avgRTI}</span> (baseline: {overallAvgRTI})
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-500 mt-0.5">
                        +{stat.avgRTI - overallAvgRTI} points higher than normal
                      </p>
                    </div>
                    <TrendingUp className="w-4 h-4 text-orange-500 dark:text-orange-400" aria-hidden="true" />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Low tension triggers */}
        {lowTensionTriggers.length > 0 && (
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="space-y-3"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-500" aria-hidden="true" />
              <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Lower Tension Triggers</h4>
            </div>
            <div className="space-y-2">
              {lowTensionTriggers.map((stat) => (
                <div
                  key={stat.trigger}
                  className="tension-glow bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-green-900 dark:text-green-300">{stat.trigger}</span>
                        <span className="text-xs text-green-600 dark:text-green-400">×{stat.count}</span>
                      </div>
                      <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                        RTI averages <span className="font-medium">{stat.avgRTI}</span> (baseline: {overallAvgRTI})
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">
                        {overallAvgRTI - stat.avgRTI} points lower than normal
                      </p>
                    </div>
                    <TrendingDown className="w-4 h-4 text-green-500 dark:text-green-400" aria-hidden="true" />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* All triggers summary */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">All Triggers</h4>
          <div className="space-y-2">
            {triggerStats.map((stat) => (
              <div
                key={stat.trigger}
                className="flex items-center justify-between py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0"
              >
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{stat.trigger}</span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-500">×{stat.count}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {stat.avgRTI}
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-500">
                      avg RTI
                    </div>
                  </div>
                  {stat.trend === 'up' && <TrendingUp className="w-4 h-4 text-orange-500" aria-hidden="true" />}
                  {stat.trend === 'down' && <TrendingDown className="w-4 h-4 text-green-500" aria-hidden="true" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Insight tip */}
        <div className="tension-glow bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg p-4">
          <p className="text-xs text-pink-900 dark:text-pink-300">
            <span className="font-medium">💡 Pro tip:</span> These patterns help you understand which life factors impact your relationship most. Use this insight to plan preventive actions.
          </p>
        </div>
      </div>
    </Card>
  );
}